import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { TGetItemFn, TPlan, TSelectPlanBlock, TSetItemFn } from '@/config/types'
import { CustomDOMEvents } from '@/enums/customDOMEvents'
import { PageName } from '@/enums/pageName'
import { SelectPlanBlockType } from '@/enums/payments'
import { useBodyOverflow } from '@/utils/domutils'

import { PrimaryButton } from '@/components/buttons/primaryButton'

import { IUser } from '@/auth/types'
import { useAuth } from '@/auth/useAuth'
import { TSelectPlanPageProps } from '@/commonPages/selectPlan/types'
import { paymentScreenVisitedKey } from '@/core/constants'
import { ISession } from '@/core/session/types'
import { getParamsFromQuery } from '@/core/session/urlManagement'
import { useSession } from '@/core/session/useSession'
import { trackAddToCart, trackPageView, trackPlanClick, trackPlanSelected } from '@/events/events'
import { useLeadEvent } from '@/events/utils'

import { PaymentBlock } from './components/paymentBlock'
import { PaymentModalBlock } from './components/paymentModalBlock'
import { SelectPlanBlock } from './components/selectPlanBlock'
import { SelectPlanBlockWithApplePay } from './components/selectPlanBlockWithApplePay'
import { SinglePlanBlock } from './components/singlePlanBlock'
import { TSelectPlanBlockControl } from './components/types'

const pageName = PageName.selectPlan

function getPlanDetails(planDetails: TPlan[], getItem: TGetItemFn, forceExtraDiscounts: boolean) {
  const showExtra = forceExtraDiscounts || Boolean(getItem(paymentScreenVisitedKey))

  return planDetails.filter((plan) => {
    if (showExtra) {
      return plan.extraDiscountPlan === true
    }

    return !plan.extraDiscountPlan
  })
}

function getDefaultPlan(planDetails: TPlan[], getItem: TGetItemFn): TPlan {
  const selectedPlanId = getItem(pageName)

  let selectedPlan: TPlan | undefined
  if (selectedPlanId) {
    selectedPlan = planDetails.find((p) => p.planId === selectedPlanId)
  }

  if (!selectedPlan) {
    selectedPlan = planDetails.find((p) => p.preselected)
  }

  return selectedPlan || planDetails[0]
}

function getPaymentModalId(blocks: TSelectPlanBlock[]): string {
  for (const block of blocks) {
    if (typeof block === 'function') {
      continue
    }
    if (block.blockType === SelectPlanBlockType.paymentModalWindow) {
      return block.modalId || block.key
    }
  }

  return ''
}

function blocksWithApplePayExists(blocks: TSelectPlanBlock[]): boolean {
  for (const block of blocks) {
    if (typeof block === 'function') {
      continue
    }
    if (block.blockType === SelectPlanBlockType.selectPlanWithPaymentForcedApplePay) {
      return true
    }
  }

  return false
}

function trackAddToCard(
  getItem: TGetItemFn,
  setItem: TSetItemFn,
  sessionContext: ISession,
  user: IUser | null
) {
  const isAddToCardSent = getItem('AddToCardSent')
  if (isAddToCardSent !== '1') {
    trackAddToCart(
      sessionContext,
      window.config.PIXEL_ID,
      window.config.TWITTER_CONF?.pixel_id,
      window.config.TIKTOK_PIXEL_ID,
      user
    )
    setItem('AddToCardSent', '1')
  }
}

export const SelectPlanPage = ({
  pageConfig,
  setItem,
  setLastVisitedItem,
  getItem,
  removeItem,
  config,
  paymentOptions,
}: TSelectPlanPageProps) => {
  const navigate = useNavigate()
  const sessionContext = useSession()
  const ref = useRef<TSelectPlanBlockControl>(null)

  const { title, nextUrl, plansDetails, buttonName, blocks } = pageConfig
  const { user } = useAuth()

  const defaultPaymentModalId = useMemo(() => getPaymentModalId(blocks), [blocks])

  const [plans, setPlans] = useState<TPlan[]>([])

  const [selectedPlan, setSelectedPlan] = useState<TPlan>(() =>
    getDefaultPlan(plansDetails, getItem)
  )

  const queryParams = getParamsFromQuery()

  const [openedModalId, setOpenedModalId] = useState<string>(() =>
    queryParams.plan_id ? defaultPaymentModalId : ''
  )
  const listenForWalletButtonClick = useMemo<boolean>(
    () => blocksWithApplePayExists(blocks),
    [blocks]
  )

  useEffect(() => {
    let activePlans = getPlanDetails(plansDetails, getItem, sessionContext.forceExtraDiscountPrice)
    if (!activePlans.length) {
      activePlans = plansDetails
    }

    setPlans(activePlans)
    setSelectedPlan(getDefaultPlan(activePlans, getItem))
  }, [plansDetails, getItem, openedModalId, sessionContext.forceExtraDiscountPrice])

  useLeadEvent(
    getItem,
    setItem,
    sessionContext,
    config.PIXEL_ID,
    config.TWITTER_CONF?.pixel_id,
    config.TIKTOK_PIXEL_ID,
    user
  )

  useEffect(() => {
    const openModalKey = getItem('openPaymentPopup')
    if (!openModalKey || !selectedPlan.planId) {
      return
    }
    setOpenedModalId(defaultPaymentModalId)
    removeItem('openPaymentPopup')
  }, [getItem, removeItem, selectedPlan, defaultPaymentModalId])

  useBodyOverflow(openedModalId)

  useEffect(
    () => {
      document.title = title
      trackPageView(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        sessionContext,
        user
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const selectPlan = (plan: TPlan) => {
    setSelectedPlan(plan)

    trackPlanClick(
      pageName,
      config.PARTNER_ID,
      config.APP_ID,
      config.PAGE_SET_ID,
      plan.internalPlanId,
      sessionContext,
      config.PIXEL_ID,
      config.TWITTER_CONF?.pixel_id,
      config.TIKTOK_PIXEL_ID,
      user
    )
  }

  const savePlan = () => {
    trackPlanSelected(
      pageName,
      [selectedPlan.name.toLowerCase()],
      config.PARTNER_ID,
      config.APP_ID,
      config.PAGE_SET_ID,
      selectedPlan.internalPlanId,
      sessionContext,
      user
    )
    trackAddToCard(getItem, setItem, sessionContext, user)
    setItem(pageName, selectedPlan.planId)
  }

  const nextPage = async () => {
    savePlan()
    nextUrl && navigate(nextUrl)
    setLastVisitedItem(pageName)
  }

  const openModal = (modalId: string) => {
    setOpenedModalId(modalId)
    savePlan()
  }

  const closeModal = () => {
    setOpenedModalId('')
  }

  const onStripeWalletButtonClickHandler = useCallback(() => {
    trackAddToCard(getItem, setItem, sessionContext, user)
    setItem(pageName, selectedPlan.planId)
  }, [getItem, setItem, sessionContext, user, selectedPlan.planId])

  useEffect(() => {
    if (!listenForWalletButtonClick) {
      return
    }
    document.addEventListener(
      CustomDOMEvents.ON_STRIPE_WALLET_BUTTON_CLICK,
      onStripeWalletButtonClickHandler
    )

    return () => {
      document.removeEventListener(
        CustomDOMEvents.ON_STRIPE_WALLET_BUTTON_CLICK,
        onStripeWalletButtonClickHandler
      )
    }
  }, [onStripeWalletButtonClickHandler, listenForWalletButtonClick])

  const className = `page page-${pageName}`

  const scrollToAnchor = () => {
    ref?.current?.scrollIntoView()
  }

  const continueButton = (
    key: string,
    className: string | undefined,
    buttonName: string | undefined
  ) => {
    let cssBtnClass = 'continue-btn '
    if (className) {
      cssBtnClass += className
    }
    return (
      <div className="button-container" key={key}>
        <PrimaryButton onClick={scrollToAnchor} className={cssBtnClass}>
          {buttonName}
        </PrimaryButton>
      </div>
    )
  }

  return (
    <div className={className}>
      {blocks.map((component) => {
        if (typeof component === 'function') {
          return component(selectedPlan, scrollToAnchor)
        } else if (component.blockType === SelectPlanBlockType.selectPlan) {
          return (
            <SelectPlanBlock
              key={component.key}
              ref={component.anchor ? ref : null}
              pageConfig={pageConfig}
              plans={plans}
              selectedPlan={selectedPlan}
              selectPlan={selectPlan}
              onSelect={nextPage}
              scrollToAnchor={scrollToAnchor}
              submitButtonClassName={component.submitButtonClassName}
              additionalBlock={component.additionalBlock}
              specificPlanDetailsTitle={component.specificPlanDetailsTitle}
              containerWrapperClassName={component.containerWrapperClassName}
            />
          )
        } else if (component.blockType === SelectPlanBlockType.selectPlanWithPayment) {
          return (
            <SelectPlanBlock
              key={component.key}
              ref={component.anchor ? ref : null}
              pageConfig={pageConfig}
              plans={plans}
              selectedPlan={selectedPlan}
              selectPlan={selectPlan}
              onSelect={() => openModal(component.modalId || component.key)}
              scrollToAnchor={scrollToAnchor}
              submitButtonClassName={component.submitButtonClassName}
              additionalBlock={component.additionalBlock}
              specificPlanDetailsTitle={component.specificPlanDetailsTitle}
              containerWrapperClassName={component.containerWrapperClassName}
            />
          )
        } else if (component.blockType === SelectPlanBlockType.singlePlanWithPayment) {
          return (
            <SinglePlanBlock
              key={component.key}
              ref={component.anchor ? ref : null}
              plans={plans}
              onSelect={() => openModal(component.modalId || component.key)}
              buttonName={buttonName}
            />
          )
        } else if (component.blockType === SelectPlanBlockType.buttonScrollToRef) {
          return continueButton(component.key, component.className, component.buttonName)
        } else if (component.blockType === SelectPlanBlockType.paymentModalWindow) {
          return (
            <PaymentModalBlock
              key={component.key}
              modalId={component.modalId || component.key}
              getItem={getItem}
              setItem={setItem}
              pageConfig={pageConfig}
              paymentOptions={paymentOptions}
              closeModal={closeModal}
              openedModalId={openedModalId}
            />
          )
        } else if (component.blockType === SelectPlanBlockType.selectPlanScrollToRef) {
          return (
            <SelectPlanBlock
              key={component.key}
              ref={component.anchor ? ref : null}
              pageConfig={pageConfig}
              plans={plans}
              selectedPlan={selectedPlan}
              selectPlan={selectPlan}
              onSelect={scrollToAnchor}
              scrollToAnchor={scrollToAnchor}
              submitButtonClassName={component.submitButtonClassName}
              additionalBlock={component.additionalBlock}
              specificPlanDetailsTitle={component.specificPlanDetailsTitle}
              containerWrapperClassName={component.containerWrapperClassName}
            />
          )
        } else if (component.blockType === SelectPlanBlockType.paymentBlock && paymentOptions) {
          return (
            <PaymentBlock
              key={component.key}
              ref={ref}
              getItem={getItem}
              setItem={setItem}
              config={window.config}
              plans={plans}
              pageName={PageName.selectPlan}
              selectedPlan={selectedPlan}
              paymentOptions={paymentOptions}
            />
          )
        } else if (
          component.blockType === SelectPlanBlockType.selectPlanWithPaymentForcedApplePay &&
          paymentOptions
        ) {
          return (
            <SelectPlanBlockWithApplePay
              key={component.key}
              ref={component.anchor ? ref : null}
              pageConfig={pageConfig}
              plans={plans}
              selectedPlan={selectedPlan}
              selectPlan={selectPlan}
              getItem={getItem}
              setItem={setItem}
              walletButtonTheme={component.walletButtonTheme}
              paymentOptions={paymentOptions}
              onSelect={() => openModal(component.modalId || component.key)}
              scrollToAnchor={scrollToAnchor}
              submitButtonClassName={component.submitButtonClassName}
              additionalBlock={component.additionalBlock}
              specificPlanDetailsTitle={component.specificPlanDetailsTitle}
              containerWrapperClassName={component.containerWrapperClassName}
            />
          )
        }
      })}
    </div>
  )
}
